import { ModalFooter as ChakraModalFooter } from '@chakra-ui/react'
import * as React from 'react'

import { BgColorProvider, useBgColorCtx } from '~theme/BgColorProvider'
import { TColorKey } from '~theme/typings'
import { shadows, TOuterShadow } from '~ui/styleGuide/shadows'
import { getSpacingValue, TSpace } from '~ui/styleGuide/spaces'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

import { useModalSettingsCtx } from '../context/ModalSettingsProvider'

type TChakraProps = Pick<PropsOf<typeof ChakraModalFooter>, 'justifyContent'>

export type TModalHeaderFooterProps = RequiredChildren &
  TChakraProps & {
    /**
     * space inside the header/footer,
     * overrides the padding that the header/footer takes from the Modal itself
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: TSpace
    /**
     * background color of the header/footer
     * also used to define the text & icon color
     */
    bgColor?: TColorKey
    /**
     * key of outer box shadows
     */
    shadow?: TOuterShadow
  }

type TProps = TModalHeaderFooterProps

/**
 * actual implementation
 * used inside a BgProviderWrapper bellow
 */
const ModalFooterComponent: React.FC<TProps> = ({
  padding,
  bgColor,
  children,
  shadow = `none`,
  ...chakraProps
}) => {
  const { bgColorValue } = useBgColorCtx()
  const { radiusValue, footerPadding } = useModalSettingsCtx()

  const shadowValue = shadows.outer[shadow]
  const paddingValue = padding ? getSpacingValue(padding) : footerPadding

  const hasBgColorSet = Boolean(bgColor)
  const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

  return (
    <ChakraModalFooter
      {...themeDesignProps}
      // TODO zIndex
      zIndex={1}
      shadow={shadowValue}
      position={`relative`}
      bgColor={hasBgColorSet ? bgColorValue : undefined}
      padding={paddingValue}
      borderBottomRadius={radiusValue}
      {...chakraProps}
    >
      {children}
    </ChakraModalFooter>
  )
}

/**
 * component for rendering the bottom section of the Modal
 * wrapping the Chakra’s ModalFooter
 * aligns its content to the right by default
 * and sync padding with the rest of the Modal if not overridden
 */
export const ModalFooter: React.FC<TProps> = (props) => {
  // this is just a BgColorProvider wrapper, the actual component is above
  return (
    <BgColorProvider bgColor={props.bgColor ?? null}>
      <ModalFooterComponent {...props} />
    </BgColorProvider>
  )
}
